/**
 * NetValue — the net-of-security figure shown beneath an asset's gross value,
 * with a click-popover that reveals the full calc:
 *
 *   <base label>            $X
 *   Less security
 *     <SI name>
 *        <creditor A>       −$a
 *        <creditor B>       −$b
 *   ─────────────────────────
 *   Net value               $J
 *
 * The security deduction is grouped by security interest and itemised by each
 * SI's linked creditors so the net traces all the way down to creditor amounts.
 * Used in the Selected Value banner, every valuation card, and the Low/High
 * recovery cards — one consistent reveal everywhere.
 */

import { Info } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/composed/overlays";
import { cn } from "@/utils/misc";
import { formatFullCurrency } from "../helpers";
import { netOfSecurity } from "../securedNetting";

// ---------------------------------------------------------------------------
// UI-facing breakdown (ids resolved to labels by the caller)
// ---------------------------------------------------------------------------

export interface SecuredCalcBreakdownUI {
  /** Σ apportioned secured balance against this asset (uncapped). */
  totalDeduction: number;
  securityInterests: Array<{
    siId: string;
    /** Secured party / SI name. */
    label: string;
    /** Optional kind / PPSR tag (e.g. "PPSR #123", "Unperfected"). */
    tag?: string;
    /** Apportioned owed this SI takes from the asset. */
    siDeduction: number;
    /** owed came from the SI's own amount, not the linked-creditor sum. */
    declaredOverride: boolean;
    creditors: Array<{ id: string; label: string; amount: number }>;
  }>;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function NetValue({
  baseValue,
  baseLabel,
  breakdown,
  size = "sm",
}: {
  /** Gross figure for this context (selected value / a valuation / low / high). */
  baseValue: number | null | undefined;
  /** Row label inside the calc, e.g. "Selected value", "Low estimate". */
  baseLabel: string;
  /** Resolved breakdown for the asset (omitted ⇒ nothing rendered). */
  breakdown?: SecuredCalcBreakdownUI;
  size?: "lg" | "sm";
}) {
  // Nothing to net against, or no gross figure to net from.
  if (baseValue == null || !breakdown || breakdown.totalDeduction <= 0) {
    return null;
  }

  const deduction = breakdown.totalDeduction;
  // The security never takes more than the asset itself is worth.
  const cappedDeduction = Math.min(deduction, baseValue);
  // Same rule the Asset table column uses, so the two never disagree.
  const net = netOfSecurity(baseValue, deduction) ?? 0;
  const exceeds = deduction > baseValue;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className={cn(
            "group flex w-fit items-center rounded-md border tabular-nums transition-colors",
            "border-border bg-background hover:border-muted-foreground/30 hover:bg-muted",
            size === "lg"
              ? "mt-2 gap-1.5 px-2.5 py-1 text-sm"
              : "mt-1 gap-1 px-1.5 py-0.5 text-[11px]",
          )}
          title="Show how the net value is calculated"
        >
          <span
            className={cn(
              "font-medium uppercase tracking-wider text-muted-foreground",
              size === "lg" ? "text-[10px]" : "text-[9px]",
            )}
          >
            Net
          </span>
          <span className="font-bold text-foreground">
            {formatFullCurrency(net)}
          </span>
          <Info
            className={cn(
              "shrink-0 text-muted-foreground/60 group-hover:text-muted-foreground",
              size === "lg" ? "h-3.5 w-3.5" : "h-3 w-3",
            )}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        onClick={(e) => e.stopPropagation()}
        className="w-80 p-0 text-sm"
      >
        <div className="space-y-2 px-4 py-3">
          {/* Base value */}
          <Row label={baseLabel} value={formatFullCurrency(baseValue)} strong />

          {/* Security deduction, grouped by SI → creditors */}
          <div className="pt-1">
            <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Less security
            </p>
            <div className="space-y-2">
              {breakdown.securityInterests.map((si) => (
                <div key={si.siId} className="space-y-1">
                  {/* SI header — name, then tag on its own line so a long PPSR
                      number never wraps awkwardly against the name. */}
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-semibold leading-tight text-foreground">
                        {si.label}
                      </p>
                      {si.tag && (
                        <p className="text-[10px] leading-tight text-muted-foreground">
                          {si.tag}
                        </p>
                      )}
                    </div>
                    {/* SI subtotal — the deduction this security interest takes
                        from the asset (Σ of its creditor lines below). */}
                    <span className="shrink-0 font-semibold tabular-nums text-rose-600 dark:text-rose-400">
                      −{formatFullCurrency(si.siDeduction)}
                    </span>
                  </div>
                  {si.creditors.length === 0 ? (
                    <p className="pl-3 text-[10px] text-muted-foreground">
                      Security interest balance
                    </p>
                  ) : (
                    <div className="ml-1 space-y-0.5 border-l border-border pl-3">
                      {si.creditors.map((c) => (
                        <div
                          key={c.id}
                          className="flex items-baseline justify-between gap-2 text-[13px]"
                        >
                          <span className="truncate text-muted-foreground">
                            {c.label}
                          </span>
                          <span className="shrink-0 tabular-nums text-muted-foreground">
                            −{formatFullCurrency(c.amount)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Total security — Σ of the SI subtotals above, so the deduction is
              visible as one figure that reconciles to the net. */}
          <div className="border-t pt-2">
            <Row
              label="Total security"
              value={`−${formatFullCurrency(deduction)}`}
              strong
              negative
            />
          </div>

          {/* Cap note when security swallows the whole asset */}
          {exceeds && (
            <div className="flex gap-1.5 rounded-md bg-amber-50 px-2 py-1.5 text-[11px] leading-snug text-amber-700 dark:bg-amber-950/30 dark:text-amber-400">
              <Info className="mt-px h-3.5 w-3.5 shrink-0" />
              <span>
                Total security ({formatFullCurrency(deduction)}) is more than
                this value, so it's capped at{" "}
                {formatFullCurrency(cappedDeduction)} and the net is $0.00.
              </span>
            </div>
          )}

          <div className="border-t pt-2">
            <Row
              label="Net value"
              value={formatFullCurrency(net)}
              strong
              emphasis
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// Bits
// ---------------------------------------------------------------------------

function Row({
  label,
  value,
  strong,
  emphasis,
  negative,
}: {
  label: string;
  value: string;
  strong?: boolean;
  emphasis?: boolean;
  /** Render the value in the deduction (rose) colour. */
  negative?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span
        className={cn(
          strong ? "font-semibold text-foreground" : "text-muted-foreground",
        )}
      >
        {label}
      </span>
      <span
        className={cn(
          "shrink-0 tabular-nums",
          emphasis
            ? "font-bold text-emerald-700 dark:text-emerald-400"
            : negative
              ? "font-semibold text-rose-600 dark:text-rose-400"
              : "font-semibold text-foreground",
        )}
      >
        {value}
      </span>
    </div>
  );
}
